import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ModuleName } from 'src/app/shared/enums/ModuleName';
import { GeneralService } from './general.service';
import { TRANSLATION_TOKEN } from '../domain/translation-token';
import { TranslationDbService } from './translation-db.service';
import { TranslationCacheService } from './translation-cache.service';

@Injectable({
  providedIn: 'root'
})

export class TranslationsService {

  translationToken = inject(TRANSLATION_TOKEN);

  constructor(
    private http: HttpClient,
    private generalService: GeneralService,
    private translationDb: TranslationDbService,
    private translationCache: TranslationCacheService
  ) {
    this.isDev = !this.translationToken.useJsonFiles;
  }

  translationAPI = this.translationToken.url;
  isDev;

  /**
   * All module names that the API returns, used for iterating
   * when storing and hydrating translations.
   */
  private readonly moduleKeys: string[] = [
    ModuleName.Assessment,
    ModuleName.Assessment180,
    ModuleName.Engagement,
    ModuleName.Happiness,
    ModuleName.Psychometric,
    ModuleName.Surveys,
    ModuleName.Exit,
    ModuleName.Common,
    ModuleName.Shared,
    ModuleName.Onboarding,
    ModuleName.Tooltips,
    ModuleName.Wellbeing,
    ModuleName.AiReadiness,
    ModuleName.SurveyX,
    ModuleName.Circle,
    ModuleName.Culture,
    'Default'
  ];

  getTranslations(): Promise<any> {
    if (this.isDev) {
      return this.getTranslationsModuleData();
    }
  }

  async getTranslationsModuleData(): Promise<any> {
    // 1. Try to load from IndexedDB first (fast, no network)
    try {
      const storedModules = await this.translationDb.getAllModules();
      if (storedModules && storedModules.length > 0) {
        for (const entry of storedModules) {
          this.translationCache.hydrate(entry.module, entry.data);
        }
        // Refresh from API in the background (stale-while-revalidate)
        this.fetchAndStore();
        return;
      }
    } catch {
      // IndexedDB unavailable, fall through to API fetch
    }

    // 2. If IndexedDB is empty, fetch from API and wait
    await this.fetchAndStore();
  }

  /**
   * Fetch translations from API, persist to IndexedDB, and hydrate memory cache.
   */
  private async fetchAndStore(): Promise<void> {
    try {
      const response = await firstValueFrom(this.getTranslationsModule());

      if (response) {
        // Store all modules to IndexedDB in a single transaction
        await this.translationDb.bulkStoreModules(response);

        // Hydrate in-memory cache for synchronous pipe access
        for (const key of this.moduleKeys) {
          if (response[key]) {
            this.translationCache.hydrate(key, response[key]);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch translations:', err);
    }
  }

  getTranslationsModule() {
    return this.http.get<Record<string, any[]>>(this.translationAPI + 'Translations/GetTranslations?path=EX');
  }
}
